import React, { useState } from 'react'
import useAxios from 'hooks/useAxios'
import EmailField from './EmailField'
import { toast } from 'libs/toast'
import { Form, Divider, Message } from 'semantic-ui-react'
import GoogleButton from './GoogleButton'
import MicrosoftButton from './MicrosoftButton'
import constants from 'config/constants'
import styled from 'styled-components'

export default function SignUpForm({ history }) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState(false)

  const { fetch: callVerifyEmail, loading: verifyEmailLoading } = useAxios({
    method: 'POST',
    url: 'account/signup-email-verification/',
    headers: {
      Authorization: '',
      'X-Workspace-Token': '',
    },
    skip: true,
  })

  const { fetch: callValidateDomain, loading: validateDomainLoading } = useAxios({
    method: 'POST',
    url: 'account/validate-domain/',
    headers: {
      Authorization: '',
      'X-Workspace-Token': '',
    },
    skip: true,
  })

  const isEmailValid = (value) => {
    const format = /^[ ]*([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})[ ]*$/i
    if (format.test(value)) {
      return true
    } else {
      return false
    }
  }

  function submitValidateDomainThenVerifyEmail() {
    if (isEmailValid(email)) {
      callValidateDomain({
        data: {
          email,
        },
      })
        .then((res) => {
          if (res.data.domain_already_exists) {
            setError(true)
          } else {
            callVerifyEmail({
              data: {
                email,
              },
            })
              .then(() => {
                history.push('/check-verify-email', { email: email })
              })
              .catch((error) => {
                if (error.response.data) {
                  toast.error(error.response.data)
                } else {
                  toast.error('Oops! Something went wrong. Please try again.')
                }
              })
          }
        })
        .catch((error) => {
          const isRateLimitedViaCloudflare =
            error.message.includes('Network Error') ||
            error.message.includes('net::ERR') ||
            error.response.status == 429

          if (isRateLimitedViaCloudflare) {
            toast.error(
              'Too many attempts detected. Your IP is temporarily blocked for 1 hour. Please try again later.'
            )
          } else if (error.response.data) {
            toast.error(error.response.data)
          } else {
            toast.error('Oops! Something went wrong. Please try again.')
          }
        })
    }
  }

  function submitVerifyEmail() {
    if (isEmailValid(email)) {
      callVerifyEmail({
        data: {
          email,
        },
      })
        .then(() => {
          history.push('/check-verify-email', { email: email })
        })
        .catch((error) => {
          if (error.response.data) {
            toast.error(error.response.data)
          } else {
            toast.error('Oops! Something went wrong. Please try again.')
          }
        })
    }
  }

  const signUpformContainer = (
    <Container>
      <img alt="SimplesatLogo" src={constants.simplesatLogoGreenUrl} width={34} />
      <h1 className="header-text">Sign up</h1>
      <p className="sub-header-text">Full-access 14-day free trial</p>

      <Form className="ui form" onSubmit={submitValidateDomainThenVerifyEmail}>
        <EmailField email={email} onChange={setEmail} />
        <button
          className="ui fluid primary submit button"
          disabled={validateDomainLoading || verifyEmailLoading}
        >
          Continue
        </button>
      </Form>
      <Divider horizontal>
        <p style={{ fontSize: 14, fontWeight: 'normal', marginTop: 5, marginBottom: 5 }}>Or</p>
      </Divider>

      <div className="social-button">
        <GoogleButton title="Sign up with Google" />
      </div>

      <div className="social-button">
        <MicrosoftButton title="Sign up with Microsoft" />
      </div>

      <div className="grey leading-[1.318]">
        <small>
          By signing up, you agree to our{'\u00a0'}
          <a href="https://www.simplesat.io/privacy" target="_blank" rel="noopener">
            privacy policy
          </a>{' '}
          and{'\u00a0'}
          <a href="https://www.simplesat.io/terms" target="_blank" rel="noopener">
            terms of service
          </a>
          .
        </small>
      </div>

      <p className="footer-text">
        Already have an account? &nbsp;{' '}
        <a
          className="cursor-pointer"
          onClick={() => history.push('/login')}
          target="_blank"
          rel="noopener"
        >
          Log in
        </a>
      </p>
    </Container>
  )

  const signUpformWithErrorContainer = (
    <ErrorContainer>
      <div>
        <img alt="SimplesatLogo" src={constants.simplesatLogoGreenUrl} width={34} />
        <h1 className="header-text">Sign up</h1>
        <p className="sub-header-text">Full-access 14-day free trial</p>
      </div>
      <Message warning>
        <Message.Header>Heads up!</Message.Header>
        <p>
          A Simplesat account already exists for the domain <b>{email.split('@').pop()}. </b>
          You can contact the account owner to be invited, or continue creating a new account.
        </p>
      </Message>
      <Form className="ui form">
        <div className="my-2">
          <p className="footer-text">
            Already have an account? &nbsp;{' '}
            <a
              className="cursor-pointer"
              onClick={() => history.push('/login')}
              target="_blank"
              rel="noopener"
            >
              Log in
            </a>
          </p>
        </div>
        <div>
          <a
            className="cursor-pointer flex items-center justify-center gap-1 my-3.5"
            onClick={submitVerifyEmail}
            target="_blank"
            rel="noopener"
          >
            Create new account
            <i className="fas fa-arrow-right icon" />
          </a>
          <div className="grey leading-[1.318]">
            <small>
              By signing up, you agree to our{'\u00a0'}
              <a href="https://www.simplesat.io/privacy" target="_blank" rel="noopener">
                privacy policy
              </a>{' '}
              and{'\u00a0'}
              <a href="https://www.simplesat.io/terms" target="_blank" rel="noopener">
                terms of service
              </a>
              .
            </small>
          </div>
        </div>
      </Form>
    </ErrorContainer>
  )

  return error ? signUpformWithErrorContainer : signUpformContainer
}

const Container = styled.div`
  padding-left: 40px;
  padding-right: 40px;

  .social-button {
    margin-bottom: 10px;
  }

  .header-text {
    margin-top: 5px;
    margin-bottom: 5px;
  }

  .sub-header-text {
    color: grey;
    font-size: 18px;
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 100%;

  padding-left: 40px;
  padding-right: 40px;

  .header-text {
    margin-top: 5px;
    margin-bottom: 5px;
  }

  .sub-header-text {
    color: grey;
    font-size: 18px;
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/containers/SignUpFormV2/index.js